import { Fragment, useEffect, useLayoutEffect, useRef } from 'react';

import * as Recharts from 'recharts';

import { useMountedState } from '@gogame/hooks-ts';

import TooltipContent from './TooltipContent';
import { sortItems } from './utils';

export const Tooltip = (props) => {
  const [active, setActive] = useMountedState(false);
  const [payload, setPayload] = useMountedState(null);
  const [position, setPosition] = useMountedState(null);
  const [tooltipRect, setTooltipRect] = useMountedState(null);
  const [mouseOver, setMouseOver] = useMountedState(false);
  const timeoutRef = useRef(0);
  const TooltipRef = useRef(null);

  useLayoutEffect(() => {
    if (TooltipRef.current && TooltipRef.current.wrapperNode) {
      setTooltipRect(TooltipRef.current.wrapperNode.getBoundingClientRect());
    }
  }, [TooltipRef.current, payload]);

  let centerOffset = -Math.min(
    ...[
      ...(tooltipRect ? [tooltipRect.width / 2] : []),
      props.viewBox.width + props.viewBox.left - props.coordinate.x,
      props.coordinate.x,
    ]
  );

  const handleMouseEnter = () => {
    setMouseOver(true);
    clearTimeout(timeoutRef.current);
  };

  const handleMouseLeave = () => {
    setMouseOver(false);
    setActive(false);
  };

  useEffect(() => {
    if (!props.active && !mouseOver) {
      timeoutRef.current = setTimeout(() => {
        setActive(false);
      }, 1000);
    } else {
      clearTimeout(timeoutRef.current);
    }
  }, [props.active]);

  useEffect(() => {
    if (props.active === true) {
      setActive(true);
    }
  }, [props.active]);

  useEffect(() => {
    if (props.payload && props.payload.length) {
      setPayload([...props.payload]);
    }
  }, [props.payload]);

  useEffect(() => {
    let tooltipWidth = (tooltipRect && tooltipRect.width) || 0;
    let tooltipHeight = (tooltipRect && tooltipRect.height) || 0;
    switch (props.posStrategy) {
      case 'top':
        setPosition({
          x: props.viewBox.width - tooltipWidth < 0 ? 0 : undefined,
          y: -tooltipHeight,
        });
        break;
      case 'bottom':
      default:
        setPosition({
          y: props.viewBox.height + 48,
          x: props.viewBox.width - tooltipWidth < 0 ? 0 : undefined,
        });
        break;
    }
  }, [props.position, props.viewBox, tooltipRect]);

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Recharts.Tooltip
        {...props}
        active={active}
        offset={centerOffset}
        payload={payload}
        position={position}
        content={(contentProps) => (
          <Fragment>
            {props.content({ ...contentProps, scrollAfterItems: 12 })}
          </Fragment>
        )}
        ref={TooltipRef}
      />
    </div>
  );
};

Tooltip.type = Recharts.Tooltip.type;
Tooltip.displayName = Recharts.Tooltip.displayName;
Tooltip.defaultProps = {
  ...Recharts.Tooltip.defaultProps,
  itemSorter: sortItems,
  content: (props) => <TooltipContent {...props} />,
  wrapperStyle: { zIndex: 100 },
  isAnimationActive: false,
  posStrategy: 'bottom',
};

export default Tooltip;
